import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, F0Checkbox } from '@factorialco/f0-react'
import { Dialog, OneModal } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import {
  MeetingsDeleteMeetingDocument,
  MeetingsValueObjectsDeletionTypeEnum,
  OneOnOnesEmployeesDocument,
  OneOnOnesScheduledMeetingsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface DeleteMeetingDialogProps {
  open: boolean
  onClose: () => void
  templateId: number
  date?: string | null
  frequency?: string | null
  meetingId?: number | null
}

export const DeleteMeetingDialog = ({
  open,
  onClose,
  templateId,
  date,
  frequency,
  meetingId,
}: DeleteMeetingDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<MeetingsValueObjectsDeletionTypeEnum>('all')
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { meetingId: currentMeetingId } = useParams<{ meetingId: string }>()

  const [deleteMeeting] = useMutation(MeetingsDeleteMeetingDocument, {
    extractorFn: (data) => data.meetingsMutations.deleteMeetingsTemplate,
    refetchQueries: [OneOnOnesScheduledMeetingsDocument, OneOnOnesEmployeesDocument],
  })

  const handleConfirmDelete = async (deletionType: MeetingsValueObjectsDeletionTypeEnum) => {
    const result = await deleteMeeting({
      variables: {
        id: templateId,
        deletionType: deletionType,
        date: deletionType === 'all' ? undefined : date,
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.meeting.edit_meeting.delete.delete_meeting_success'),
      })

      // Handle navigation after successful deletion
      if (meetingId === Number(currentMeetingId)) {
        navigate(navigation.engagement.one_on_ones.fullPath())
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    onClose()
  }

  if (frequency) {
    return (
      <OneModal isOpen={open} onClose={onClose}>
        <OneModal.Header
          title={i18n.t('meetings.meeting.edit_meeting.delete.delete_event_title')}
        />
        <OneModal.Content withPadding>
          <Box gap="s12" flexDirection="column" paddingY="s12" alignItems="flexStart">
            <Box gap="s12" flexDirection="column">
              <F0Checkbox
                checked={selectedOption === 'one'}
                onCheckedChange={() => setSelectedOption('one')}
                title={i18n.t('meetings.meeting.edit_meeting.delete.this_event')}
              />
              <F0Checkbox
                checked={selectedOption === 'future'}
                onCheckedChange={() => setSelectedOption('future')}
                title={i18n.t('meetings.meeting.edit_meeting.delete.this_and_following_events')}
              />
              <F0Checkbox
                checked={selectedOption === 'all'}
                onCheckedChange={() => setSelectedOption('all')}
                title={i18n.t('meetings.meeting.edit_meeting.delete.all_events')}
              />
            </Box>
          </Box>
        </OneModal.Content>
        <OneModal.Footer>
          <F0Button onClick={() => onClose()} label={i18n.t('common.cancel')} variant="outline" />
          <F0Button
            onClick={() => handleConfirmDelete(selectedOption)}
            label={i18n.t('common.confirm')}
            variant="default"
          />
        </OneModal.Footer>
      </OneModal>
    )
  }

  return (
    <Dialog
      onClose={onClose}
      actions={{
        primary: {
          icon: Delete,
          label: i18n.t('meetings.meeting.edit_meeting.delete.delete_meeting_title'),
          onClick: () => handleConfirmDelete('all'),
          variant: 'critical',
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: onClose,
        },
      }}
      header={{
        description: i18n.t('meetings.meeting.edit_meeting.delete.delete_meeting_description'),
        title: i18n.t('meetings.meeting.edit_meeting.delete.delete_meeting_button'),
        type: 'critical',
      }}
      open={open}
    />
  )
}
